import React from "react";
import { Skeleton } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";

/**
 * Stands in for the details container while an edit is being saved.
 *
 * A confirmed edit recalculates the report, so several values change at once —
 * report status, and anything derived from the edited figures. Leaving the old
 * numbers on screen until the refetch lands would show values that are already
 * wrong, so the container is skeletoned instead.
 *
 * Shaped like the real thing: three sections of 10px-label / 12px-value fields
 * in three columns, separated by the same hairline, so nothing shifts when the
 * data arrives. Uses MUI `Skeleton`, as the record table's row skeleton does.
 */
const SECTION_ROWS = [3, 2, 2];

const PnoeDetailsSkeleton = () => {
  const theme: Theme = useTheme();

  return (
    <div style={styles.container(theme)} aria-busy="true">
      {SECTION_ROWS.map((rows, sectionIndex) => (
        <React.Fragment key={sectionIndex}>
          {sectionIndex > 0 && <div style={styles.divider(theme)} />}
          <div style={styles.section}>
            <Skeleton
              variant="rectangular"
              sx={{
                width: "140px",
                height: "20px",
                borderRadius: "4px",
                backgroundColor: theme.colorTokens.N[30],
              }}
            />
            <div style={styles.columns}>
              {[0, 1, 2].map((column) => (
                <div key={column} style={styles.column}>
                  {Array.from({ length: rows }).map((_, row) => (
                    <div key={row} style={styles.field}>
                      <Skeleton
                        variant="rectangular"
                        sx={{
                          width: "72px",
                          height: "10px",
                          borderRadius: "4px",
                          backgroundColor: theme.colorTokens.N[30],
                        }}
                      />
                      <Skeleton
                        variant="rectangular"
                        sx={{
                          width: "50%",
                          height: "14px",
                          borderRadius: "4px",
                          backgroundColor: theme.colorTokens.N[30],
                        }}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

export default PnoeDetailsSkeleton;

const styles = {
  container: (theme: Theme) => ({
    padding: "16px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
  }),
  divider: (theme: Theme) => ({
    height: "1px",
    backgroundColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
  }),
  section: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
  columns: {
    display: "flex",
    alignItems: "flex-start",
    gap: "24px",
  },
  column: {
    flex: "1 1 0",
    minWidth: 0,
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
  field: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
  },
};
